import { useState } from 'react'
import { Card, Button, Space, Typography, Divider } from 'antd'
import { SettingOutlined } from '@ant-design/icons'
import {
  FieldConfigurationDrawer,
  EditableFieldSelect
} from '@/components/FieldConfiguration'

const { Title, Paragraph, Text } = Typography

export function FieldConfigurationDemo() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedField, setSelectedField] = useState<string>('')

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Card>
        <Title level={2}>Field Configuration Components Demo</Title>
        <Paragraph>
          This page demonstrates the field label configuration components for customizing
          field labels and visibility across different table contexts.
        </Paragraph>

        <Divider />

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Phase 6: Field Configuration Drawer */}
          <Card title="Phase 6: Field Configuration Drawer" size="small">
            <Paragraph>
              <Text strong>Description:</Text> Full-featured field configuration UI with
              table-based editing, bulk operations, and search/filter capabilities.
            </Paragraph>
            <Paragraph>
              <Text strong>Features:</Text>
              <ul>
                <li>Inline label editing (click to edit)</li>
                <li>Visibility toggle switches</li>
                <li>Customization badges</li>
                <li>Bulk operations (hide/show/reset multiple fields)</li>
                <li>Search and filter</li>
                <li>Expandable row details</li>
                <li>Reset to default for custom labels</li>
              </ul>
            </Paragraph>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => setDrawerOpen(true)}
            >
              Open Field Configuration Drawer
            </Button>
          </Card>

          {/* Phase 7: Inline Label Editor */}
          <Card title="Phase 7: Inline Label Editor" size="small">
            <Paragraph>
              <Text strong>Description:</Text> Lightweight inline editing within field
              selection dropdowns for quick label customization during data mapping.
            </Paragraph>
            <Paragraph>
              <Text strong>Features:</Text>
              <ul>
                <li>Edit icon next to each field option</li>
                <li>Popover-based editor (no navigation away)</li>
                <li>Label and visibility editing in one place</li>
                <li>Immediate dropdown refresh after save</li>
                <li>Customization badges in dropdown</li>
              </ul>
            </Paragraph>
            <div style={{ maxWidth: 400 }}>
              <Text>Select a field (click the edit icon to customize):</Text>
              <br />
              <br />
              <EditableFieldSelect
                context="offerings"
                value={selectedField}
                onChange={setSelectedField}
                placeholder="Select an offering field"
                style={{ width: '100%' }}
              />
            </div>
          </Card>

          {/* Usage Example */}
          <Card title="Usage Example" size="small">
            <Paragraph>
              <Text strong>Importing the components:</Text>
            </Paragraph>
            <pre
              style={{
                background: '#f5f5f5',
                padding: 16,
                borderRadius: 4,
                overflow: 'auto'
              }}
            >
              {`import {
  FieldConfigurationDrawer,
  EditableFieldSelect,
  InlineLabelEditor
} from '@/components/FieldConfiguration'

// Phase 6: Full configuration drawer
<FieldConfigurationDrawer
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  context="offerings"
  contextDisplayName="Offerings"
/>

// Phase 7: Inline editor in dropdown
<EditableFieldSelect
  context="offerings"
  value={selectedField}
  onChange={setSelectedField}
  placeholder="Select field"
/>`}
            </pre>
          </Card>

          {/* Testing Notes */}
          <Card title="Testing Notes" size="small" type="inner">
            <Paragraph>
              <Text strong>Test Context:</Text> This demo uses the "offerings" table
              context.
            </Paragraph>
            <Paragraph>
              <Text strong>Data Source:</Text> Field labels are fetched from{' '}
              <code>/field_configurations/labels/offerings</code>
            </Paragraph>
            <Paragraph>
              <Text strong>Caching:</Text> React Query caches labels for 24 hours. Changes
              invalidate the cache and refetch automatically.
            </Paragraph>
            <Paragraph>
              <Text type="warning">
                Note: Some features require backend API endpoints to be deployed (update
                and delete operations).
              </Text>
            </Paragraph>
          </Card>
        </Space>
      </Card>

      {/* Field Configuration Drawer */}
      <FieldConfigurationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        context="offerings"
        contextDisplayName="Offerings"
      />
    </div>
  )
}

export default FieldConfigurationDemo
